<template>
  <BContainer
    fluid
    class="mb-4"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5
        class="fw-bold mb-0"
        style="color: #1e293b;"
      >
        My Appointments
      </h5>
      <BButton
        variant="primary"
        size="sm"
        @click="openBookingModal"
      >
        <i class="fas fa-plus me-1" />
        Book Appointment
      </BButton>
    </div>

    <!-- Booking Form Modal -->
    <BModal
      v-model="showBookingForm"
      title="Book New Appointment"
      size="lg"
      modal-class="student-form-modal"
      centered
      hide-footer
    >
      <BCardText>
        <h6 class="fw-bold mb-3">
          <i class="fas fa-calendar-plus text-primary me-2" />
          Book New Appointment
        </h6>
        <ValidationObserver ref="formBooking">
          <form
            novalidate
            role="form"
            @submit.prevent
          >
            <BRow>
              <BCol cols="12">
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="slot_id"
                  name="appointment slot"
                  rules="required"
                >
                  <BFormGroup
                    label="Select Appointment Slot"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <!-- Selected Slot Display / Select Button -->
                    <div
                      v-if="selectedSlotInfo"
                      class="selected-slot-card d-flex align-items-center justify-content-between p-3 rounded border"
                      style="background: #f0fdf4; border-color: #22c55e !important;"
                    >
                      <div>
                        <i class="fas fa-check-circle text-success me-2" />
                        <strong>{{ selectedSlotInfo.date }}</strong>
                        <span class="text-muted mx-2">|</span>
                        <span>{{ selectedSlotInfo.time }}</span>
                      </div>
                      <BButton
                        variant="outline-primary"
                        size="sm"
                        @click="openSlotCalendar"
                      >
                        <i class="fas fa-edit me-1" />
                        Change
                      </BButton>
                    </div>
                    <BButton
                      v-else
                      variant="outline-primary"
                      class="w-100 py-3"
                      :disabled="state.loadingSlots"
                      @click="openSlotCalendar"
                    >
                      <i :class="['fas me-2', state.loadingSlots ? 'fa-spinner fa-spin' : 'fa-calendar-alt']" />
                      {{ state.loadingSlots ? 'Loading...' : 'Select Date & Time' }}
                    </BButton>
                    <input
                      type="hidden"
                      :value="bookingForm.slot_id"
                    >
                    <div
                      v-if="errors.length > 0"
                      class="text-danger small mt-1"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="concern_type_id"
                  name="concern type"
                  rules="required"
                >
                  <BFormGroup
                    label="Concern Type"
                    label-for="concern_type_id"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <BFormSelect
                      id="concern_type_id"
                      v-model="bookingForm.concern_type_id"
                      :options="concernTypeOptions"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.booking || state.loadingConcernTypes"
                    >
                      <template #first>
                        <BFormSelectOption
                          :value="null"
                          disabled
                        >
                          {{ state.loadingConcernTypes ? 'Loading...' : 'Select concern type' }}
                        </BFormSelectOption>
                      </template>
                    </BFormSelect>
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol
                v-if="bookingForm.concern_type_id === 'other'"
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="other_concern"
                  name="concern details"
                  :rules="bookingForm.concern_type_id === 'other' ? 'required|max:255' : ''"
                >
                  <BFormGroup
                    label="Please Specify"
                    label-for="other_concern"
                    label-class="font-weight-bold"
                    class="mb-2"
                  >
                    <BFormInput
                      id="other_concern"
                      v-model="bookingForm.other_concern"
                      placeholder="Describe your concern..."
                      maxlength="255"
                      autocomplete="off"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.booking"
                    />
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>

              <BCol cols="12">
                <BFormGroup
                  label="Description (Optional)"
                  label-for="description"
                  label-class="font-weight-bold"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="description"
                    v-model="bookingForm.description"
                    placeholder="Briefly describe your concern..."
                    rows="3"
                    :disabled="state.booking"
                  />
                </BFormGroup>
              </BCol>
            </BRow>

            <div class="d-flex justify-content-end">
              <BButton
                variant="outline-secondary"
                class="me-2"
                :disabled="state.booking"
                @click="showBookingForm = false"
              >
                Cancel
              </BButton>
              <BButton
                variant="primary"
                :disabled="state.booking"
                @click.prevent="onValidateBooking"
              >
                <i :class="['fas me-1', state.booking ? 'fa-spinner fa-spin' : 'fa-calendar-check']" />
                {{ state.booking ? 'Booking...' : 'Book Appointment' }}
              </BButton>
            </div>
          </form>
        </ValidationObserver>
      </BCardText>
    </BModal>

    <!-- Appointment List -->
    <BCard class="border shadow-sm">
      <BCardText>
        <BOverlay
          :show="state.loading"
          opacity="0.5"
        >
          <BTable
            :items="appointments"
            :fields="tableFields"
            responsive
            stacked="md"
            show-empty
            hover
            class="mb-0"
          >
            <template #empty>
              <div class="text-center py-4">
                <i
                  class="fas fa-calendar-times mb-2"
                  style="font-size: 2rem; color: #cbd5e1;"
                />
                <p
                  class="small mb-0"
                  style="color: #94a3b8;"
                >
                  No appointments found
                </p>
              </div>
            </template>

            <template #cell(reference_number)="{ value }">
              <span class="fw-bold text-primary">{{ value }}</span>
            </template>

            <template #cell(appointment_slot)="{ item }">
              <span v-if="getAppointmentSlot(item)">
                {{ getAppointmentSlot(item).date }}<br>
                <small class="text-muted">
                  {{ formatTime(getAppointmentSlot(item).start_time) }} - {{ formatTime(getAppointmentSlot(item).end_time) }}
                </small>
              </span>
              <span v-else>—</span>
            </template>

            <template #cell(appointment_concern_type)="{ item }">
              {{ getAppointmentConcernType(item) ? getAppointmentConcernType(item).name : 'Other' }}
            </template>

            <template #cell(status)="{ value }">
              <BBadge :variant="statusVariant(value)">
                {{ value }}
              </BBadge>
            </template>

            <template #cell(actions)="{ item }">
              <BButton
                v-if="item.status === 'for_confirmation'"
                variant="outline-danger"
                size="sm"
                :disabled="state.cancelling"
                @click="onCancelAppointment(item)"
              >
                <i class="fas fa-times" />
              </BButton>
            </template>
          </BTable>
        </BOverlay>

        <!-- Pagination -->
        <div
          v-if="pagination.last_page > 1"
          class="d-flex justify-content-end mt-3"
        >
          <BPagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            size="sm"
            @change="onPageChange"
          />
        </div>
      </BCardText>
    </BCard>

    <!-- Slot Calendar Modal -->
    <BModal
      id="slot-calendar-modal"
      v-model="showSlotCalendar"
      title="Select Appointment Date & Time"
      size="lg"
      centered
      hide-footer
    >
      <div
        v-if="state.loadingSlots"
        class="text-center py-5"
      >
        <i class="fas fa-spinner fa-spin fa-2x mb-2 text-primary" />
        <p class="text-muted">
          Loading available slots...
        </p>
      </div>

      <div
        v-else-if="availableSlots.length === 0"
        class="text-center py-5"
      >
        <i
          class="fas fa-calendar-times fa-3x mb-3"
          style="color: #cbd5e1;"
        />
        <p class="text-muted">
          No available slots at the moment
        </p>
      </div>

      <div v-else>
        <SlotCalendar
          :slots="availableSlots"
          :selected-slot-id="bookingForm.slot_id"
          :selectable="true"
          :show-slots-inline="true"
          :initial-date="new Date()"
          @date-select="calendarSelectedDate = $event"
          @slot-select="selectSlotFromCalendar"
        />
        <div class="text-center py-3 text-muted">
          <i class="fas fa-hand-pointer me-1" />
          Tap any available slot chip inside a date cell to select it.
        </div>
      </div>
    </BModal>
  </BContainer>
</template>

<script>
import MxMisc from '@/mixins/misc'
import SlotCalendar from '@/components/custom/SlotCalendar.vue'
import { SStudentAppointment } from '@/services/student'
import { SSharedList } from '@/services/shared'

export default {
  name: 'StudentAppointments',

  components: { SlotCalendar },

  mixins: [MxMisc],

  middleware: ['auth', 'student'],

  metaInfo: () => ({
    title: 'My Appointments'
  }),

  data () {
    return {
      state: {
        loading: false,
        booking: false,
        cancelling: false,
        loadingSlots: false,
        loadingConcernTypes: false
      },
      showBookingForm: false,
      showSlotCalendar: false,
      isMobileCalendar: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
      calendarSelectedDate: null,
      bookingForm: {
        slot_id: null,
        concern_type_id: null,
        other_concern: '',
        description: ''
      },
      appointments: [],
      availableSlots: [],
      concernTypes: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      },
      tableFields: [
        { key: 'reference_number', label: 'Reference #', sortable: true },
        { key: 'appointment_slot', label: 'Schedule' },
        { key: 'appointment_concern_type', label: 'Concern' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'actions', label: '', class: 'text-center' }
      ]
    }
  },

  computed: {
    concernTypeOptions () {
      const opts = this.concernTypes.map(ct => ({
        value: ct.id,
        text: ct.name
      }))
      opts.push({ value: 'other', text: 'Other (please specify)' })
      return opts
    },

    selectedSlotInfo () {
      if (!this.bookingForm.slot_id) return null
      const slot = this.availableSlots.find(s => s.id === this.bookingForm.slot_id)
      if (!slot) return null
      return {
        date: this.formatSlotDate(slot.date),
        time: `${this.formatTime(slot.start_time)} — ${this.formatTime(slot.end_time)}`
      }
    }
  },

  mounted () {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onViewportResize)
    }
    this.fetchAppointments()
  },

  beforeDestroy () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onViewportResize)
    }
  },

  methods: {
    onViewportResize () {
      this.isMobileCalendar = window.innerWidth < 768
    },

    openBookingModal () {
      this.showBookingForm = true
      this.fetchSlots()
      this.fetchConcernTypes()
    },

    formatSlotDate (dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    },

    formatTime (time) {
      if (!time) return ''
      const [hours, minutes] = time.substring(0, 5).split(':')
      const h = parseInt(hours, 10)
      const suffix = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${h12}:${minutes} ${suffix}`
    },

    statusVariant (status) {
      switch (status) {
        case 'scheduled': return 'success'
        case 'for_confirmation': return 'warning'
        case 'canceled': return 'danger'
        case 'completed': return 'info'
        default: return 'secondary'
      }
    },

    async fetchAppointments () {
      this.state.loading = true
      try {
        const { data } = await SStudentAppointment.getMyAppointments({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        })

        const items = data.items || data.data || []
        this.appointments = items
        this.pagination.total = data.total_rows || data.total || items.length
        this.pagination.per_page = data.per_page || this.pagination.per_page
        this.pagination.current_page = data.current_page || this.pagination.current_page
        this.pagination.last_page = Math.max(1, Math.ceil(this.pagination.total / this.pagination.per_page))
      } catch {
        this.appointments = []
      } finally {
        this.state.loading = false
      }
    },

    async fetchSlots () {
      this.state.loadingSlots = true
      try {
        const { data } = await SStudentAppointment.getAvailableSlots()
        this.availableSlots = (data.data || []).map(slot => ({
          ...slot,
          is_available: slot.is_available ?? true,
          active_appointments_count: slot.active_appointments_count ?? 0
        }))
      } catch {
        this.availableSlots = []
      } finally {
        this.state.loadingSlots = false
      }
    },

    async fetchConcernTypes () {
      this.state.loadingConcernTypes = true
      try {
        const { data } = await SSharedList.getActiveAppointmentConcernTypes({ per_page: 500, sort: 'name', sort_desc: false })
        this.concernTypes = data.data || []
      } catch {
        this.concernTypes = []
      } finally {
        this.state.loadingConcernTypes = false
      }
    },

    onPageChange (page) {
      this.pagination.current_page = page
      this.fetchAppointments()
    },

    async onValidateBooking (event) {
      event.preventDefault()
      await this.$refs.formBooking.validate().then(
        async validForm => {
          if (!validForm) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: '<h6>Book this appointment?</h6>',
            confirmButtonText: 'Book',
            preConfirm: () => {
              this.state.booking = true
              return this.onPostBooking()
            }
          })
        }
      )
    },

    async onPostBooking () {
      return new Promise(resolve => {
        const payload = { ...this.bookingForm }
        if (payload.concern_type_id === 'other') {
          payload.concern_type_id = null
          payload.description = payload.other_concern + (payload.description ? '\n' + payload.description : '')
        }
        delete payload.other_concern
        SStudentAppointment.book(payload).then(
          ({ data }) => {
            const msg = data.message || 'Appointment booked successfully.'
            const refNum = data.referenceNumber
              ? `<br><small>Reference: <strong>${data.referenceNumber}</strong></small>`
              : ''
            this.swalSuccess(`${msg}${refNum}`).then(() => {
              this.showBookingForm = false
              this.bookingForm = { slot_id: null, concern_type_id: null, other_concern: '', description: '' }
              this.$refs.formBooking.reset()
              this.pagination.current_page = 1
              this.fetchAppointments()
            })
          }
        ).catch(
          ({ message }) => {
            if (this.$refs.formBooking && message?.errors) {
              this.$refs.formBooking.setErrors(message.errors)
            }
            resolve(message)
          }
        ).finally(() => {
          this.state.booking = false
        })
      })
    },

    onCancelAppointment (appointment) {
      this.swalConfirm({
        html: '<h6>Cancel this appointment?</h6><p class="small text-muted">This action cannot be undone.</p>',
        confirmButtonText: 'Yes, Cancel',
        customClass: {
          title: 'h5 p-2 text-danger border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: () => {
          this.state.cancelling = true
          return SStudentAppointment.cancel(appointment.id).then(
            ({ data }) => {
              this.swalSuccess(data.message || 'Appointment cancelled.')
              this.fetchAppointments()
            }
          ).catch(
            ({ message }) => {
              this.swalInvalid(message?.message || 'Failed to cancel appointment.')
            }
          ).finally(() => {
            this.state.cancelling = false
          })
        }
      })
    },

    openSlotCalendar () {
      this.showSlotCalendar = true
      this.calendarSelectedDate = null
      if (this.availableSlots.length === 0) {
        this.fetchSlots()
      }
    },

    selectSlotFromCalendar (slot) {
      this.bookingForm.slot_id = slot.id
      this.calendarSelectedDate = slot.date
      this.showSlotCalendar = false
    },

    getAppointmentSlot (item) {
      return item?.appointment_slot || item?.appointmentSlot || item?.slot || null
    },

    getAppointmentConcernType (item) {
      return item?.appointment_concern_type || item?.appointmentConcernType || item?.concern_type || null
    }
  }
}
</script>

<style scoped>
:deep(.student-form-modal .modal-header) {
  border-bottom: 1px solid #e2e8f0;
}

:deep(.student-form-modal .modal-footer) {
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 575.98px) {
  :deep(.student-form-modal .modal-dialog) {
    margin: 0;
    max-width: 100%;
    min-height: 100vh;
  }

  :deep(.student-form-modal .modal-content) {
    min-height: 100vh;
    border-radius: 0;
  }
}
</style>
